import {
  AvroOptional,
  log as signalsLogRenderError,
  ProviderId,
} from "@indeed/signals-schema/com/indeed/signals/schema/reactnative/RNRenderErrorV5";

import {
  DEFAULT_ERROR_NAME,
  EMPTY_STACK_TRACE,
  getErrorMessage,
  getErrorName,
  getErrorStackTrace,
} from "../../../shared/logging/DatadogErrorUtils";
import { DatadogLogger } from "../../../shared/logging/DatadogLogger";
import { getDeviceProperties } from "../../../shared/logging/LoggableDeviceProperties";
import { sanitizeError } from "../../../shared/logging/RenderErrorBoundary";

/**
 * Error sources for Datadog RUM error classification.
 * These help categorize errors in the Datadog dashboard.
 */
export enum ErrorSource {
  /** JavaScript runtime errors (e.g., TypeError, ReferenceError) */
  SOURCE = "source",
  /** Network-related errors (e.g., fetch failures, timeouts) */
  NETWORK = "network",
  /** Agent/SDK errors */
  AGENT = "agent",
  /** Errors logged via console.error() */
  CONSOLE = "console",
  /** Errors from logging libraries */
  LOGGER = "logger",
  /** Errors from WebView components */
  WEBVIEW = "webview",
  /** Custom application errors */
  CUSTOM = "custom",
}

/**
 * GlobalErrorHandler intercepts all JavaScript errors and console.error calls,
 * sending them to both Indeed Signals and Datadog for monitoring.
 *
 * This handler is initialized once at app startup and automatically captures:
 * - Fatal JavaScript crashes (via ErrorUtils.setGlobalHandler)
 * - Non-fatal console.error() calls
 *
 * All captured errors are sent to:
 * 1. Indeed Signals (for internal analytics)
 * 2. Datadog RUM (for error tracking in RUM dashboard)
 * 3. Datadog Logs (for searchable error logs)
 *
 * @example Automatic error capture
 * ```typescript
 * // Fatal errors are automatically captured:
 * throw new Error('Something went wrong'); // Captured and reported
 *
 * // Console errors are automatically captured:
 * console.error('Failed to load data', error); // Captured and reported
 *
 * // try-catch errors should be logged via console.error:
 * try {
 *   await fetchData();
 * } catch (error) {
 *   console.error('fetchData failed', error); // Captured and reported
 * }
 * ```
 *
 * @example Using console.error (RECOMMENDED)
 * ```typescript
 * // Simple message
 * console.error('User authentication failed');
 *
 * // With error object (automatically extracts name, message, stack)
 * console.error('Network request failed', new Error('Timeout'));
 *
 * // With multiple arguments (all concatenated)
 * console.error('API Error:', error, 'User ID:', userId);
 *
 * // The handler will automatically:
 * // 1. Extract error name, message, and stack trace
 * // 2. Send to Datadog RUM with source='console'
 * // 3. Send to Datadog Logs with full context
 * // 4. Call original console.error for debugging
 * // 5. Log to Indeed Signals for analytics
 * ```
 */
class GlobalErrorHandler {
  private isInitialized = false;
  private originalHandler: ((error: any, isFatal?: boolean) => void) | null = null;
  private originalConsoleError: ((...params: unknown[]) => void) | null = null;
  private providerIds: Set<string> = new Set();
  private isInDefaultErrorHandler = false;

  /**
   * Initialize the global error handler. Safe to call multiple times.
   */
  public initialize(): void {
    if (this.isInitialized) {
      return;
    }

    // 1. Setup Global Error Handler
    this.originalHandler = ErrorUtils.getGlobalHandler();
    ErrorUtils.setGlobalHandler((error, isFatal) => {
      this.handleError(error, isFatal);
    });

    // 2. Setup Console Error Interceptor
    this.originalConsoleError = console.error;
    console.error = this.onConsoleError;

    this.isInitialized = true;
  }

  /**
   * Handle the global error by logging it to Signals and Datadog, then calling the original handler.
   */
  private handleError(error: any, isFatal?: boolean): void {
    // A. Log to Signals
    try {
      const { message, name, stack = "" } = sanitizeError(error);

      signalsLogRenderError({
        message: "GlobalErrorHandler: " + message,
        name,
        stack,
        componentStack: "", // Global errors don't have component stack
        deviceProperties: getDeviceProperties(),
        provider: AvroOptional.string("Unknown"),
        providerId: { ProviderId: ProviderId.Unknown },
      });
    } catch (loggingError) {
      // Don't let logging errors break error handling
      console.warn("Error in global error handler logging to Signals:", loggingError);
    }

    // B. Log to Datadog
    const message = getErrorMessage(error);
    const stacktrace = getErrorStackTrace(error);
    const errorName = getErrorName(error);

    this.reportDatadogError(message, ErrorSource.SOURCE, stacktrace, errorName, {
      "_dd.error.is_crash": isFatal,

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      "_dd.error.raw": error,
    })
      .then(() => {
        // On real iOS devices, the crash context is not updated soon
        // enough for the view update to contain the crash.
        // Waiting for 50ms has low impact and ensures the crash context
        // is updated before actually crashing the app.
        setTimeout(() => {
          this.isInDefaultErrorHandler = true;
          try {
            if (this.originalHandler) {
              this.originalHandler(error, isFatal);
            }
          } finally {
            this.isInDefaultErrorHandler = false;
          }
        }, 50);
      })
      .catch((_e) => {
        console.warn("Failed to report global error to Datadog", _e);
        // Even if reporting fails, ensure we call original handler
        if (this.originalHandler) {
          this.originalHandler(error, isFatal);
        }
      });
  }

  /**
   * Intercept console.error calls to log non-fatal errors to Datadog.
   *
   * @param params - All arguments passed to console.error()
   *                 Can be strings, Error objects, or any other values
   *
   * @example
   * console.error('Simple message');
   * console.error('Error occurred:', error);
   * console.error('User ID:', userId, 'failed with', error);
   */
  private onConsoleError = (...params: unknown[]): void => {
    if (this.isInDefaultErrorHandler) {
      return;
    }

    let stack: string = EMPTY_STACK_TRACE;
    let errorName: string = DEFAULT_ERROR_NAME;

    // Extract stack trace and error name from any Error objects in params
    for (let i = 0; i < params.length; i += 1) {
      const param = params[i];
      const paramStack = getErrorStackTrace(param);
      if (paramStack !== EMPTY_STACK_TRACE) {
        stack = paramStack;
      }

      const paramErrorName = getErrorName(param);
      if (paramErrorName !== DEFAULT_ERROR_NAME) {
        errorName = paramErrorName;
      }

      if (errorName !== DEFAULT_ERROR_NAME && stack !== EMPTY_STACK_TRACE) {
        break;
      }
    }

    // Concatenate all params into a single message
    const message = params
      .map((param) => {
        if (typeof param === "string") {
          return param;
        } else {
          return getErrorMessage(param);
        }
      })
      .join(" ");

    if (this.originalConsoleError) {
      this.originalConsoleError.apply(console, params);
    }

    // Report to Datadog asynchronously (fire and forget)
    this.reportDatadogError(message, ErrorSource.CONSOLE, stack, errorName).catch((_e) => {
      // Silently ignore Datadog reporting errors to avoid infinite loops
      // (we already called originalConsoleError above)
    });
  };

  /**
   * Report an error to Datadog (both RUM and Logs).
   *
   * @param message - The error message
   * @param source - Error source category (SOURCE, CONSOLE, NETWORK, etc.)
   * @param stacktrace - Full stack trace string
   * @param errorName - Error type/name (e.g., 'TypeError', 'NetworkError')
   * @param context - Additional context to attach to the error
   */
  private reportDatadogError = async (
    message: string,
    source: ErrorSource,
    stacktrace: string,
    errorName: string,
    context: Record<string, unknown> = {},
  ): Promise<void> => {
    await Promise.all([
      DatadogLogger.addRumError(message, source, stacktrace, context),
      DatadogLogger.error(message, context, errorName, message, stacktrace),
    ]);
  };

  /**
   * Reset the global error handler to its original state.
   * Should only be called when the app is shutting down.
   */
  public reset(): void {
    if (!this.isInitialized) {
      return;
    }

    if (this.originalHandler) {
      ErrorUtils.setGlobalHandler(this.originalHandler);
    }

    if (this.originalConsoleError) {
      console.error = this.originalConsoleError;
    }

    this.isInitialized = false;
    this.originalHandler = null;
    this.originalConsoleError = null;
    this.providerIds.clear();
  }

  public addProviderId(providerId: string): void {
    this.providerIds?.add(providerId);
  }
}

export const globalErrorHandler = new GlobalErrorHandler();

/**
 * Simple function to initialize the global error handler.
 * It's supposed to be called once in the register.ts file.
 */
export function initializeGlobalErrorHandler(): void {
  globalErrorHandler.initialize();
}
